/**
 * Stripe Checkout Service
 * Creates a Stripe Checkout Session and redirects to hosted Stripe payment page
 * Matches sensorpushweb implementation exactly
 */

// Use Netlify function URL pattern (same as sensorpushweb)
// In production (Netlify), functions are served from '/.netlify/functions'
// In local development, we expect you to run `netlify dev`, which exposes functions on port 8888
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? '/.netlify/functions'
    : 'http://localhost:8888/.netlify/functions';

/**
 * Create a Stripe Checkout Session
 * @param {Object} orderData - Order data including items, shippingInfo, total
 * @param {string} userId - Optional user ID
 * @returns {Promise<{sessionUrl: string, sessionId: string}>} - Stripe Checkout Session URL and ID
 */
export const createStripeCheckoutSession = async (orderData, userId) => {
  try {
    console.log('💳 Creating Stripe checkout session:', { url: `${API_BASE_URL}/stripe-create-checkout-session`, orderData, userId });
    
    const response = await fetch(`${API_BASE_URL}/stripe-create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: orderData.items,
        total: orderData.total,
        shippingInfo: orderData.shippingInfo,
        userId: userId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.message || `Failed to create checkout session: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ Stripe checkout session created:', data);
    
    // Return both sessionUrl and sessionId (matching sensorpushweb)
    return {
      sessionUrl: data.sessionUrl,
      sessionId: data.sessionId,
    };
  } catch (error) {
    console.error('❌ Error creating Stripe checkout session:', error);
    throw error;
  }
};

/**
 * Verify a Stripe Checkout Session
 * @param {string} sessionId - Stripe Checkout Session ID
 * @returns {Promise<Object>} - Session details including payment status and metadata
 */
export const verifyCheckoutSession = async (sessionId) => {
  try {
    console.log('🔍 Verifying checkout session:', sessionId);
    
    const response = await fetch(`${API_BASE_URL}/stripe-verify-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.message || `Failed to verify checkout session: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ Checkout session verified:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error verifying checkout session:', error);
    throw error;
  }
};

/**
 * Redirect to Stripe Checkout
 * @param {Object} orderData - Order data with items, shippingInfo, total
 * @param {string} userId - Optional user ID
 */
export const redirectToStripeCheckout = async (orderData, userId) => {
  try {
    const { sessionUrl } = await createStripeCheckoutSession(orderData, userId);
    console.log('✅ Checkout session created, redirecting to:', sessionUrl);
    // Redirect to Stripe hosted checkout page (same as sensorpushweb)
    window.location.href = sessionUrl;
  } catch (error) {
    console.error('❌ Error redirecting to Stripe:', error);
    throw error;
  }
};

